/**
 * Iter XX.75 — Practice with Gully debug view (founder-only).
 *
 * Lists the last 50 practice sessions for the signed-in user and
 * lets the founder inspect each one's raw Gemini JSON alongside the
 * conversational reply. Gated by `practice_with_gully_enabled`
 * (server-side 404 for anyone else).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import api from "../lib/api";

function StatusBadge({ status }) {
  const map = {
    complete: "bg-emerald-900/40 text-emerald-300 border-emerald-800/70",
    analysing: "bg-amber-900/40 text-amber-200 border-amber-800/70",
    failed: "bg-rose-900/40 text-rose-200 border-rose-800/70",
  };
  const cls = map[status] || "bg-neutral-800 text-neutral-300 border-neutral-700";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${cls}`}
      data-testid={`practice-debug-status-${status}`}
    >
      {status || "?"}
    </span>
  );
}

function Field({ label, value, mono = true }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-neutral-900 last:border-b-0">
      <div className="col-span-1 text-[11px] uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div
        className={
          "col-span-2 text-[12px] text-neutral-200 break-all " +
          (mono ? "font-mono" : "")
        }
      >
        {value === null || value === undefined || value === ""
          ? <span className="text-neutral-600">—</span>
          : String(value)}
      </div>
    </div>
  );
}

function SessionCard({ s, expanded, onToggle }) {
  const g = s.structured || {};
  const gate = g._gate || {};
  return (
    <div
      className="rounded-xl border border-neutral-800 bg-neutral-950/60 overflow-hidden"
      data-testid={`practice-debug-card-${s.practice_id}`}
    >
      <button
        onClick={() => onToggle(s.practice_id)}
        className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-neutral-900/60 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-mono text-[11px] text-neutral-400"
              data-testid={`practice-debug-id-${s.practice_id}`}
            >
              {s.practice_id}
            </span>
            <StatusBadge status={s.status} />
            <span className="text-[11px] text-neutral-500">
              focus: {s.focus}
            </span>
          </div>
          <div className="mt-1 text-[11px] text-neutral-500">
            {s.created_at}
            {s.completed_at && ` → ${s.completed_at}`}
          </div>
          {s.reply && (
            <div className="mt-2 text-[13px] text-neutral-200 line-clamp-2">
              {s.reply}
            </div>
          )}
        </div>
        <div className="text-neutral-500 text-lg leading-none">
          {expanded ? "−" : "+"}
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-neutral-900 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
              Safety gate
            </div>
            <Field label="video_quality" value={gate.video_quality} />
            <Field label="confidence" value={gate.confidence} />
            <Field label="clearly_observed" value={gate.clearly_observed_deliveries} />
            <Field label="unsafe_reason" value={gate.unsafe_reason} />
            <Field label="safe_to_cue (final)" value={String(g.safe_to_cue)} />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
              Structured (from Gemini)
            </div>
            <Field label="observation" value={g.observation} mono={false} />
            <Field
              label="supporting_evidence"
              value={(g.supporting_evidence || []).join(" · ")}
              mono={false}
            />
            <Field label="coaching_cue" value={g.coaching_cue} mono={false} />
            <Field
              label="total_estimated_deliveries"
              value={g.total_estimated_deliveries}
            />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
              Conversational reply (from narrator)
            </div>
            <div
              className="rounded-lg bg-neutral-900/70 border border-neutral-800 px-3 py-2 text-[13px] text-neutral-100 whitespace-pre-wrap"
              data-testid={`practice-debug-reply-${s.practice_id}`}
            >
              {s.reply || <span className="text-neutral-600">— empty —</span>}
            </div>
          </div>

          {s.raw_gemini_text && (
            <details className="rounded-lg border border-neutral-800 bg-black/40">
              <summary className="px-3 py-2 text-[11px] uppercase tracking-wider text-neutral-500 cursor-pointer">
                Raw Gemini text
              </summary>
              <pre
                className="px-3 py-2 text-[11px] font-mono text-neutral-300 whitespace-pre-wrap break-all"
                data-testid={`practice-debug-raw-${s.practice_id}`}
              >
                {s.raw_gemini_text}
              </pre>
            </details>
          )}

          {s.error && (
            <div
              className="rounded-lg bg-rose-950/40 border border-rose-800/60 px-3 py-2 text-[12px] text-rose-100 font-mono"
              data-testid={`practice-debug-error-${s.practice_id}`}
            >
              {s.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PracticeDebug() {
  const [state, setState] = useState({ loading: true, error: null, sessions: [] });
  const [expandedId, setExpandedId] = useState(null);

  async function load() {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data } = await api.get("/web/practice/debug/list");
      setState({ loading: false, error: null, sessions: data?.sessions || [] });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "unknown_error";
      setState({
        loading: false,
        error: status === 404
          ? "Practice with Gully is not enabled for your account."
          : status === 401
          ? "Please sign in first."
          : `Failed to load practice sessions (${detail})`,
        sessions: [],
      });
    }
  }

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-500 mb-1">
              Founder Debug
            </div>
            <h1 className="text-2xl font-semibold">
              Practice with Gully — sessions
            </h1>
            <p className="text-[12px] text-neutral-500 mt-1">
              Latest 50 sessions for this account. Raw Gemini JSON is
              compared against the conversational reply.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className="rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-100 text-sm px-3 py-1.5"
              data-testid="practice-debug-refresh"
            >
              Refresh
            </button>
            <Link
              to="/chat"
              className="rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-100 text-sm px-3 py-1.5"
              data-testid="practice-debug-back-to-chat"
            >
              Back to chat
            </Link>
          </div>
        </div>

        {state.loading && (
          <div
            className="text-sm text-neutral-500"
            data-testid="practice-debug-loading"
          >
            Loading…
          </div>
        )}

        {state.error && (
          <div
            className="rounded-xl border border-amber-800/60 bg-amber-950/30 text-amber-100 px-4 py-3 text-sm"
            data-testid="practice-debug-error"
          >
            {state.error}
          </div>
        )}

        {!state.loading && !state.error && state.sessions.length === 0 && (
          <div
            className="rounded-xl border border-neutral-800 bg-neutral-900/40 px-4 py-6 text-sm text-neutral-400 text-center"
            data-testid="practice-debug-empty"
          >
            No practice sessions yet. Upload an over from the chat
            surface to see it here.
          </div>
        )}

        {!state.loading && !state.error && state.sessions.length > 0 && (
          <div className="space-y-2" data-testid="practice-debug-list">
            {state.sessions.map((s) => (
              <SessionCard
                key={s.practice_id}
                s={s}
                expanded={expandedId === s.practice_id}
                onToggle={(id) => setExpandedId(expandedId === id ? null : id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
